import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Menu,
  X,
  Sparkles,
  User as UserIcon,
  ChevronDown,
  ExternalLink,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import './Navbar.css';
import { FiMenu, FiX, FiUser } from 'react-icons/fi';
import logo from '../../Assets/logo-update.png';
const API_BASE_URL = process.env.REACT_APP_BACKEND_API_URL;
const navLinks = [
  { label: "AI Agent", href: "/aiAgents" },
  { label: "Integrations", href: "#integrations" },
  { label: "Pricing", href: "/pricing" },
  { label: "Book a Demo", href: "/bookdemo" },
];
const Navbar = () => {
    const navigate = useNavigate();

  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileIntegrationsOpen, setMobileIntegrationsOpen] = useState(false);
  const [user, setUser] = useState(null);

  /* Load user */
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) setUser(JSON.parse(storedUser));
  }, []);

  /* Scroll effect */
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSignIn = () => navigate("/SignIn");

  const handleBuildChatbot = async () => {
    if (!user) {
      localStorage.setItem("redirectAfterLogin", "/BuildChatBot");
      return navigate("/SignIn");
    }

    try {
      const res = await fetch(
        `${API_BASE_URL}/api/auth/get-by-email/${user.email}`
      );
      if (res.status === 404) {
        navigate("/BuildChatBot");
        return;
      }
      if (!res.ok) throw new Error("Fetch failed");

      const data = await res.json();
      navigate(data?.botName || data?.botImageName ? "/dashboard" : "/BuildChatBot");
    } catch (err) {
      console.error("Bot check failed", err);
      navigate("/BuildChatBot");
    }
  };


  return (
  <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`modern-navbar ${isScrolled ? "scrolled" : ""}`}
      >
        <div className="navbar-container">
          {/* LOGO */}
          <Link to="/" className="navbar-logo">
            <div className="logo-icon">
               {/* <img src={logo} alt="Xenachat Logo" className="sparkle-icon" /> */}
            </div>
            <span className="logo-text">
              Xena<span className="logo-gradient">Chat</span>
            </span>
          </Link>

          {/* DESKTOP LINKS */}
          <div className="navbar-links">
            <Link to="/aiAgents" className="nav-link">AI Agent</Link>

            {/* Integrations Dropdown */}
           <div className="nav-link dropdown">
              <span>Integrations</span>

              <div className="dropdown-menu dark-dropdown">
                <Link to="/integrations/whatsapp">WhatsApp</Link>
                <Link to="/integrations/telegram">Telegram</Link>
              </div>
            </div>

            <Link to="/pricing" className="nav-link">Pricing</Link>
            <Link to="/bookdemo" className="nav-link">Book a Demo</Link>
          </div>

          {/* DESKTOP ACTIONS */}
          <div className="navbar-actions">
            <button className="btn-build" onClick={handleBuildChatbot}>
              <Sparkles className="btn-icon" />
              Build Your AI Chatbot
            </button>

            {user ? (
              <button
                className="btn-admin"
                onClick={() => navigate("/profile")}
              >
                <UserIcon className="admin-icon" />
                {user.name}
              </button>
            ) : (
              <button className="btn-admin" onClick={handleSignIn}>
                Sign In
              </button>
            )}
          </div>

          {/* MOBILE TOGGLE */}
          <button
            className="mobile-menu-btn"
            onClick={() => setIsMobileMenuOpen(true)}
          >
            <Menu className="menu-icon" />
          </button>
        </div>
      </motion.nav>

      {/* MOBILE MENU */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              className="mobile-menu-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => {
                setIsMobileMenuOpen(false);
                setMobileIntegrationsOpen(false);
              }}
            />

            <motion.div
              className="mobile-menu"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
            >
              <div className="mobile-menu-header">
                <span className="logo-text">XenaChat</span>
                <button
                  className="mobile-close-btn"
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    setMobileIntegrationsOpen(false);
                  }}
                >
                  <X />
                </button>
              </div>

             <div className="mobile-menu-content">
              <div className="mobile-links">
                <Link
                  className="mobile-link"
                  to="/aiAgents"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  AI Agent
                </Link>

                <button
                  className={`mobile-accordion ${mobileIntegrationsOpen ? "open" : ""}`}
                  onClick={() => setMobileIntegrationsOpen(!mobileIntegrationsOpen)}
                  type="button"
                >
                  <span>Integrations</span>
                  <ChevronDown className="accordion-icon" />
                </button>

                {mobileIntegrationsOpen && (
                  <div className="mobile-accordion-content">
                    <Link
                      to="/integrations/whatsapp"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      WhatsApp
                    </Link>
                    <Link
                      to="/integrations/telegram"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Telegram
                    </Link>
                  </div>
                )}

                <Link
                  className="mobile-link"
                  to="/pricing"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Pricing
                </Link>

                <Link
                  className="mobile-link"
                  to="/bookdemo"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Book a Demo
                </Link>
              </div>

              <div className="mobile-actions">
                {user ? (
                  <button
                    className="mobile-btn-admin"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate("/profile");
                    }}
                  >
                    <UserIcon className="admin-icon" />
                    {user.name}
                  </button>
                ) : (
                  <button
                    className="mobile-btn-admin"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      handleSignIn();
                    }}
                  >
                    <UserIcon className="admin-icon" />
                    Sign In
                  </button>
                )}

                <button
                  className="mobile-btn-build"
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    handleBuildChatbot();
                  }}
                >
                  Build Your AI Chatbot
                </button>
              </div>
            </div>

            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
